/**
 * Categories - the one `categories` table that spec 09's partners and spec 10's
 * FamilyBiz listings are both filed under.
 *
 * There is no scope: any active category can hold a partner or a listing. That
 * is why this is its own Directory section rather than a card on either of
 * those screens - an admin adding "Restaurants" for a partner should see it is
 * available to member listings too.
 *
 * Retiring a category is `is_active = false`, not Delete: the server refuses a
 * delete while any live partner still uses it (409 `category_in_use`).
 */

import { useState } from "react";

import { PrimaryCell } from "../components/DataTable";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  SuccessNotice,
} from "../components/primitives";
import {
  createCategory,
  deleteCategory,
  fetchCategories,
  updateCategory,
  type Category,
} from "../lib/api/vendors";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";

type CategoryChanges = Parameters<typeof updateCategory>[1];

export function CategoriesScreen() {
  const query = useQuery<{ items: Category[]; total: number }>(() => fetchCategories(), []);
  const [editing, setEditing] = useState<{ category: Category | null } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const toggle = useMutation(
    (category: Category) => updateCategory(category.id, { is_active: !category.is_active }),
    {
      onSuccess: (result) => {
        setSaved(result?.message ?? "Category updated.");
        query.reload();
      },
    },
  );

  const remove = useMutation((id: string) => deleteCategory(id), {
    onSuccess: (result) => {
      setSaved(result?.message ?? "Category deleted.");
      query.reload();
    },
  });

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}
      {toggle.error ? <ErrorNotice error={toggle.error} /> : null}
      {remove.error ? <ErrorNotice error={remove.error} /> : null}

      <Card flush>
        <div style={{ padding: "24px 24px 16px" }}>
          <CardHeader
            title="Categories"
            hint="Shared by partners and FamilyBiz listings; any active category can hold either."
            actions={
              <div className="row row--tight">
                <Button size="sm" onClick={query.reload} disabled={query.refreshing}>
                  {query.refreshing ? "Refreshing…" : "Refresh"}
                </Button>
                <Button size="sm" variant="accent" onClick={() => setEditing({ category: null })}>
                  New category
                </Button>
              </div>
            }
          />
        </div>

        {query.error ? <ErrorNotice error={query.error} onRetry={query.reload} /> : null}

        {query.loading && !query.data ? <LoadingRows rows={6} columns={5} /> : null}

        {query.data && query.data.items.length === 0 ? (
          <EmptyState
            title="No categories yet."
            body="Partners and listings can be uncategorised, but a category is what makes the member directory navigable."
            action={
              <Button size="sm" variant="accent" onClick={() => setEditing({ category: null })}>
                New category
              </Button>
            }
          />
        ) : null}

        {query.data && query.data.items.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Sort order
                  </th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Partners
                  </th>
                  <th scope="col">Active</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((category) => (
                  <tr key={category.id}>
                    <td>
                      <PrimaryCell title={category.name} detail={category.slug} />
                    </td>
                    <td className="table__num">{category.display_order}</td>
                    <td className="table__num">{category.vendor_count}</td>
                    <td>
                      {category.is_active ? (
                        <Badge tone="active">Active</Badge>
                      ) : (
                        <Badge tone="muted">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      <div className="table__actions">
                        <Button size="sm" onClick={() => setEditing({ category })}>
                          Edit
                        </Button>
                        <Button size="sm" onClick={() => void toggle.run(category)}>
                          {category.is_active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={() => {
                            // The server refuses this while partners still use it
                            // (409 `category_in_use`), with the count in the sentence.
                            if (confirmAction(`Delete the category "${category.name}"?`)) {
                              void remove.run(category.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {editing ? (
        <CategoryFormDialog
          category={editing.category}
          onClose={() => setEditing(null)}
          onDone={(message) => {
            setEditing(null);
            setSaved(message);
            query.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function CategoryFormDialog({
  category,
  onClose,
  onDone,
}: {
  category: Category | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const isNew = category === null;
  const [form, setForm] = useState({
    name: category?.name ?? "",
    slug: category?.slug ?? "",
    display_order: String(category?.display_order ?? 0),
  });

  /**
   * Edit sends only what changed, so an untouched field is never rewritten (and
   * never audited as a change). A cleared slug is left out rather than sent: the
   * column is NOT NULL and the API has no "regenerate" path for an existing row,
   * so blank on edit means "keep the current one".
   */
  function changes(existing: Category): CategoryChanges {
    const out: CategoryChanges = {};
    const name = form.name.trim();
    const slug = form.slug.trim();
    const order = Number(form.display_order) || 0;
    if (name !== existing.name) out.name = name;
    if (slug && slug !== existing.slug) out.slug = slug;
    if (order !== existing.display_order) out.display_order = order;
    return out;
  }

  const save = useMutation(
    () => {
      if (category === null) {
        return createCategory({
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          display_order: Number(form.display_order) || 0,
        });
      }
      return updateCategory(category.id, changes(category));
    },
    { onSuccess: (result) => onDone(result?.message ?? (isNew ? "Category created." : "Category saved.")) },
  );

  const unchanged = category !== null && Object.keys(changes(category)).length === 0;

  return (
    <Modal
      title={isNew ? "New category" : `Edit ${category.name}`}
      description="Changes reach the member app on its next request; nothing is shipped in the app itself."
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Name (required)">
            {(id) => (
              <Input
                id={id}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                maxLength={80}
              />
            )}
          </Field>
          <Field
            label="Link name"
            hint={
              isNew
                ? "Optional. Generated from the name if left empty."
                : "Leave empty to keep the current link name."
            }
          >
            {(id) => (
              <Input
                id={id}
                value={form.slug}
                onChange={(event) => setForm({ ...form, slug: event.target.value })}
                maxLength={80}
              />
            )}
          </Field>
          <Field label="Sort order">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={form.display_order}
                onChange={(event) => setForm({ ...form, display_order: event.target.value })}
              />
            )}
          </Field>
        </div>
        <ErrorNotice error={save.error} />
        <div className="row row--end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="accent"
            disabled={!form.name.trim() || unchanged || save.pending}
            onClick={() => void save.run()}
          >
            {save.pending ? "Saving…" : isNew ? "Create category" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
